// The schedule-call station's booked face for the booking-confirmation
// test arm: when the call is, who is calling, what to expect, and the
// questions the family can answer ahead of the call. The kicker and title
// belong to the station's ScreenChrome.

import { useEffect, useRef, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { CalendarDaysIcon, PlayIcon } from "@heroicons/react/24/solid";

import VideoLightbox, { type VideoCloseInfo } from "components/VideoLightbox";
import { ButtonSpinner } from "components/Button";
import { CONTINUE_BUTTON_CLASS, FOCUS_RING_CLASS } from "components/FormV2";
import config from "config";

import { useQuoteFlowAnalytics } from "./analytics";
import type { QuoteCheckpoint, ScheduledCall } from "./api";
import { putCheckpoint } from "./api";
import {
  BOOKING_CONFIRMATION_COPY as COPY,
  PREP_QUESTIONS,
  calendarEventDescription,
} from "./booking-confirmation-copy";
import { OUTLINE_BUTTON_CLASS } from "components/FlowButtons";
import {
  MAIN_GUTTER_CLASS,
  NARROW_MAIN_WIDTH_CLASS,
} from "components/ScreenChrome";
import {
  calendarKindsFor,
  calendarLabel,
  googleCalendarUrl,
  icsUrl,
  outlookCalendarUrl,
  type CalendarKind,
} from "./calendar-links";
import {
  browserTimeZone,
  callDayLabel,
  callZoneAbbreviation,
  slotTimeLabel,
} from "./call-time";
import {
  PRE_CALL_ANSWERS_KIND,
  isEmpty,
  isSelected,
  toggleAnswer,
  type PreCallAnswers,
  type PrepQuestionId,
} from "./pre-call-answers";
import { createPreCallAnswersSaver } from "./pre-call-answers-saver";
import {
  highlightSegments,
  repFirstName,
  reviewForRep,
  type CallRep,
} from "./specialists";
import {
  TESTIMONIAL_POSTER,
  TESTIMONIAL_THUMBNAIL,
  testimonialVideoSources,
} from "./testimonial-video";

const SAVE_DEBOUNCE_MS = 600;
/** How long Start my application waits for a pending answers save before
 * moving on without it. */
const FLUSH_WAIT_MS = 1500;

const BLOCK_LABEL_CLASS =
  "mb-3 text-sm font-bold uppercase tracking-[0.12em] text-oat-800";

export interface BookingConfirmationProps {
  quoteId: string;
  call: ScheduledCall;
  rep: CallRep;
  /** The lead's number as the quote shows it. */
  contactPhone: string | null;
  marketSlug: string | null;
  initialAnswers: PreCallAnswers;
  onAnswersSaved: (checkpoint: QuoteCheckpoint) => void;
  /** Writes the call gate and enters the application; settles once the
   * flow has moved on. */
  onStartApplication: () => Promise<void>;
}

function Phone({ phone }: { phone: string | null }) {
  return <span className="ph-no-capture">{phone ?? "you"}</span>;
}

function WhenCard({
  quoteId,
  call,
  rep,
  contactPhone,
}: Pick<
  BookingConfirmationProps,
  "quoteId" | "call" | "rep" | "contactPhone"
>) {
  const { track } = useQuoteFlowAnalytics();
  const [timeZone] = useState(browserTimeZone);
  const [open, setOpen] = useState(false);
  const [kinds] = useState(() => calendarKindsFor(navigator.userAgent));
  const zone = callZoneAbbreviation(call.start, timeZone);
  const event = {
    title: COPY.calendarEventTitle,
    details: calendarEventDescription(repFirstName(rep), contactPhone),
    startIso: call.start,
  };

  const hrefFor = (kind: CalendarKind): string => {
    if (kind === "google") return googleCalendarUrl(event);
    if (kind === "outlook") return outlookCalendarUrl(event);
    return icsUrl(config.apiEndpoint, quoteId);
  };

  return (
    <section className="rounded-2xl border-2 border-evergreen bg-oat p-4 2xs:p-5">
      <div className="flex items-center gap-3 2xs:gap-3.5">
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-evergreen 2xs:h-[42px] 2xs:w-[42px]">
          <CheckIcon
            className="h-5 w-5 text-daisy"
            strokeWidth={3}
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0">
          <div className="text-xl font-bold leading-tight">
            {callDayLabel(call.start, timeZone)}
          </div>
          <div className="whitespace-nowrap text-[28px] font-bold leading-[1.1] tracking-[-0.01em] text-soil 2xs:text-[32px] xs:text-[34px]">
            {slotTimeLabel(call.start, timeZone)}
            {zone ? ` ${zone}` : ""}
          </div>
        </div>
      </div>
      <p className="mt-3 text-soil">
        {COPY.timeLineLead} <Phone phone={contactPhone} />.
      </p>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="booking-calendar-options"
        onClick={() => setOpen((current) => !current)}
        // The box padding is trimmed (important, to beat the shared box class) so
        // the label and its icon share a line inside the card at 320.
        className={`${OUTLINE_BUTTON_CLASS} mt-4 min-h-[52px] w-full whitespace-nowrap !px-4 2xs:!px-[26px]`}
      >
        <CalendarDaysIcon className="h-5 w-5 flex-none" aria-hidden="true" />
        {COPY.addToCalendarLabel}
      </button>
      <div
        id="booking-calendar-options"
        hidden={!open}
        className={`ph-no-capture mt-2.5 flex-col gap-2 ${open ? "flex" : "hidden"}`}
      >
        {kinds.map((kind) => (
          <a
            key={kind}
            href={hrefFor(kind)}
            {...(kind === "apple"
              ? {}
              : { target: "_blank", rel: "noopener noreferrer" })}
            onClick={() =>
              track("quote_flow_booking_calendar_opened", { calendar: kind })
            }
            className={`flex min-h-[52px] items-center rounded-[10px] border-[1.5px] border-oat-200 bg-white px-4 text-[17px] font-bold leading-tight text-soil hover:border-soil ${FOCUS_RING_CLASS}`}
          >
            {calendarLabel(kind)}
            {kind !== "apple" && (
              <span className="sr-only"> {COPY.opensInNewTab}</span>
            )}
          </a>
        ))}
      </div>
    </section>
  );
}

function RepAvatar({ rep }: { rep: CallRep }) {
  if (rep.photo) {
    return (
      <img
        src={rep.photo}
        alt=""
        className="h-14 w-14 flex-none rounded-full bg-oat object-cover 2xs:h-16 2xs:w-16 xs:h-[72px] xs:w-[72px]"
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-evergreen font-serif text-2xl leading-none text-ivory-light 2xs:h-16 2xs:w-16 2xs:text-[28px] xs:h-[72px] xs:w-[72px] xs:text-[30px]"
    >
      {rep.name.charAt(0).toUpperCase()}
    </div>
  );
}

function HostCard({ rep }: { rep: CallRep }) {
  return (
    <section>
      <h2 className={BLOCK_LABEL_CLASS}>{COPY.hostHeading}</h2>
      <div className="rounded-2xl border border-oat-200 bg-white p-4 2xs:p-5">
        <div className="flex flex-col items-start gap-3 2xs:flex-row 2xs:items-center 2xs:gap-3.5">
          <RepAvatar rep={rep} />
          <div className="min-w-0">
            <div className="text-xl font-bold leading-tight 2xs:text-[22px]">
              {rep.name}
            </div>
            <div className="mt-0.5 text-sm text-oat-700 2xs:text-[15px]">
              {COPY.hostRole}
            </div>
          </div>
        </div>
        <p className="mt-4">{COPY.hostBio}</p>
      </div>
    </section>
  );
}

function ReviewCard({
  rep,
  marketSlug,
}: {
  rep: CallRep;
  marketSlug: string | null;
}) {
  const review = reviewForRep(rep, marketSlug);
  return (
    <figure className="m-0 flex flex-col gap-3.5 rounded-xl bg-evergreen px-4 py-5 2xs:px-5 2xs:py-6">
      <div
        aria-hidden="true"
        className="text-[19px] leading-none tracking-[2px] text-daisy"
      >
        {"★★★★★"}
      </div>
      <span className="sr-only">{COPY.reviewStarsLabel}</span>
      <blockquote className="m-0 font-serif text-lg leading-[1.35] text-ivory-light 2xs:text-[22px]">
        &ldquo;
        {highlightSegments(review.quote, review.highlight).map(
          (segment, index) =>
            segment.highlight ? (
              <span
                key={index}
                className="font-sans text-[1.05em] font-bold text-daisy"
              >
                {segment.text}
              </span>
            ) : (
              <span key={index}>{segment.text}</span>
            ),
        )}
        &rdquo;
      </blockquote>
      <figcaption className="text-base text-oat">{review.author}</figcaption>
    </figure>
  );
}

function VideoCard() {
  const { track } = useQuoteFlowAnalytics();
  const [open, setOpen] = useState(false);
  const [opener, setOpener] = useState<HTMLElement | null>(null);
  const [sources] = useState(() =>
    testimonialVideoSources(
      config.cdnHost,
      (
        navigator as Navigator & {
          connection?: { saveData?: boolean; effectiveType?: string };
        }
      ).connection,
    ),
  );

  return (
    <section>
      <h2 className={BLOCK_LABEL_CLASS}>{COPY.videoHeading}</h2>
      <button
        type="button"
        onClick={(event) => {
          track("quote_flow_booking_video_opened", {});
          setOpener(event.currentTarget);
          setOpen(true);
        }}
        className={`flex w-full items-center gap-3 rounded-2xl border border-oat-200 bg-white p-3 text-left text-soil 2xs:gap-4 ${FOCUS_RING_CLASS}`}
      >
        <span className="relative aspect-[9/16] w-20 flex-none overflow-hidden rounded-[10px] bg-oat 2xs:w-24">
          <img
            src={TESTIMONIAL_THUMBNAIL}
            alt=""
            className="block h-full w-full object-cover"
          />
          <span className="absolute inset-0 m-auto flex h-11 w-11 items-center justify-center rounded-full bg-ivory-light/90 text-soil shadow-md">
            <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" />
          </span>
        </span>
        <span className="flex min-w-0 flex-col gap-2.5">
          <span className="font-serif text-lg leading-tight 2xs:text-[22px]">
            &ldquo;{COPY.videoQuote}&rdquo;
          </span>
          <span className="text-[15px] font-bold text-evergreen">
            {COPY.videoMeta}
            <span className="sr-only">, {COPY.videoLengthLabel}</span>
          </span>
        </span>
      </button>
      <VideoLightbox
        open={open}
        sources={sources}
        poster={TESTIMONIAL_POSTER}
        title={COPY.videoHeading}
        returnFocusTo={opener}
        closeLabel={COPY.videoClose}
        onClose={(info: VideoCloseInfo) => {
          setOpen(false);
          track("quote_flow_booking_video_closed", {
            seconds_watched: info.secondsWatched,
            completed: info.completed,
            via: info.via,
          });
        }}
      />
    </section>
  );
}

function ExpectList() {
  return (
    <section>
      <h2 className={BLOCK_LABEL_CLASS}>{COPY.expectHeading}</h2>
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {COPY.expectPoints.map((point) => (
          <li key={point} className="flex items-start gap-3">
            <CheckIcon
              className="mt-[3px] h-[22px] w-[22px] flex-none text-evergreen"
              strokeWidth={2.6}
              aria-hidden="true"
            />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PrepBand({
  quoteId,
  initialAnswers,
  onAnswersSaved,
  saveBeforeLeaving,
}: Pick<BookingConfirmationProps, "quoteId" | "initialAnswers"> & {
  onAnswersSaved: BookingConfirmationProps["onAnswersSaved"];
  saveBeforeLeaving: { current: () => Promise<void> };
}) {
  const { track, reportFailure } = useQuoteFlowAnalytics();
  const [answers, setAnswers] = useState(initialAnswers);
  const [saved, setSaved] = useState(initialAnswers);
  const latest = useRef({ onAnswersSaved, reportFailure });
  latest.current = { onAnswersSaved, reportFailure };

  const [saver] = useState(() =>
    createPreCallAnswersSaver({
      saved: initialAnswers,
      delayMs: SAVE_DEBOUNCE_MS,
      save: async (next) => {
        const { checkpoint } = await putCheckpoint(
          quoteId,
          PRE_CALL_ANSWERS_KIND,
          next,
          { keepalive: true },
        );
        latest.current.onAnswersSaved(checkpoint);
      },
      onSaved: setSaved,
      onFailed: (error) =>
        latest.current.reportFailure("pre_call_answers", error),
    }),
  );

  useEffect(() => {
    saveBeforeLeaving.current = () => saver.flush();
  }, [saver, saveBeforeLeaving]);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void saver.flush();
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      void saver.flush();
    };
  }, [saver]);

  const tap = (id: PrepQuestionId, value: string) => {
    const next = toggleAnswer(answers, id, value);
    if (next === answers) return;
    setAnswers(next);
    saver.schedule(next);
    track("quote_flow_pre_call_answer_toggled", {
      question: id,
      option: value,
      selected: isSelected(next, id, value),
    });
  };

  return (
    <div className="ml-[calc(50%-50vw)] w-screen">
      <img
        src="/public/help-bg-top.webp"
        alt=""
        aria-hidden="true"
        className="block h-auto w-full"
      />
      <div className="relative -my-px bg-ivory pb-7 pt-5">
        <div className={`${MAIN_GUTTER_CLASS} ${NARROW_MAIN_WIDTH_CLASS}`}>
          <h2 className="m-0 font-serif text-[25px] font-normal leading-[1.15] text-soil 2xs:text-[28px]">
            {COPY.prepHeading}
          </h2>
          <p className="mt-3 text-oat-700">{COPY.prepSub}</p>
          {PREP_QUESTIONS.map((question) => (
            <fieldset
              key={question.id}
              className="m-0 mt-10 min-w-0 border-0 p-0"
            >
              <legend className="p-0 text-lg font-bold leading-snug">
                <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                  {question.question}
                  {question.multi && (
                    <span className="rounded-full border border-oat-200 px-2 py-0.5 text-xs font-bold uppercase tracking-[0.08em] text-oat-700">
                      {COPY.multiTag}
                    </span>
                  )}
                </span>
              </legend>
              <div className="mt-4 grid grid-cols-2 gap-2.5 2xs:gap-3">
                {question.options.map((option) => {
                  const pressed = isSelected(
                    answers,
                    question.id,
                    option.value,
                  );
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={pressed}
                      onClick={() => tap(question.id, option.value)}
                      className={`flex min-h-[56px] items-center rounded-[10px] border px-3 py-2.5 text-left font-serif text-[15px] leading-tight transition-colors 2xs:px-3.5 2xs:text-[17px] ${FOCUS_RING_CLASS} ${
                        pressed
                          ? "border-evergreen bg-evergreen text-ivory-light"
                          : "border-soil/[0.18] bg-white text-soil"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
          <p
            aria-live="polite"
            className="mt-6 min-h-[27px] font-bold text-evergreen"
          >
            {isEmpty(saved) ? "" : COPY.prepSaved}
          </p>
        </div>
      </div>
      <img
        src="/public/help-bg-bottom.webp"
        alt=""
        aria-hidden="true"
        className="block h-auto w-full"
      />
    </div>
  );
}

function StartApplication({
  saveBeforeLeaving,
  onStartApplication,
}: {
  saveBeforeLeaving: { current: () => Promise<void> };
  onStartApplication: () => Promise<void>;
}) {
  const { reportFailure } = useQuoteFlowAnalytics();
  const [starting, setStarting] = useState(false);
  const startingRef = useRef(false);

  const start = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    try {
      await Promise.race([
        saveBeforeLeaving.current(),
        new Promise((resolve) => setTimeout(resolve, FLUSH_WAIT_MS)),
      ]);
      await onStartApplication();
    } catch (error) {
      reportFailure("start_application", error);
      startingRef.current = false;
      setStarting(false);
    }
  };

  return (
    <section>
      <div className="rounded-xl bg-oat px-4 py-4 2xs:px-5">
        <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-oat-800">
          {COPY.appBoxHeading}
        </h2>
        <p className="mt-2">{COPY.appBoxBody}</p>
      </div>
      <button
        type="button"
        aria-busy={starting || undefined}
        onClick={() => void start()}
        className={`${CONTINUE_BUTTON_CLASS} mt-4 min-h-[52px] w-full !px-4 text-lg 2xs:!px-[26px]`}
      >
        {starting && <ButtonSpinner />}
        {COPY.appBoxCta}
      </button>
    </section>
  );
}

export default function BookingConfirmation({
  quoteId,
  call,
  rep,
  contactPhone,
  marketSlug,
  initialAnswers,
  onAnswersSaved,
  onStartApplication,
}: BookingConfirmationProps) {
  const saveBeforeLeaving = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  );
  return (
    <div className="mt-6 flex flex-col gap-10 text-lg 2xs:gap-14">
      <WhenCard
        quoteId={quoteId}
        call={call}
        rep={rep}
        contactPhone={contactPhone}
      />
      <div className="flex flex-col gap-4">
        <HostCard rep={rep} />
        <ReviewCard rep={rep} marketSlug={marketSlug} />
      </div>
      <VideoCard />
      <ExpectList />
      <PrepBand
        quoteId={quoteId}
        initialAnswers={initialAnswers}
        onAnswersSaved={onAnswersSaved}
        saveBeforeLeaving={saveBeforeLeaving}
      />
      <StartApplication
        saveBeforeLeaving={saveBeforeLeaving}
        onStartApplication={onStartApplication}
      />
    </div>
  );
}
